import React, { useState } from 'react';
import { useServiceSelector, BusinessSize, AIExperience, Implementation, Budget, PrimaryGoal } from '../../context/ServiceSelectorContext';
import { Link } from 'react-router-dom';

// Create interface for step content
interface Step {
  title: string;
  description: string;
  field: 'businessSize' | 'aiExperience' | 'implementation' | 'budget' | 'primaryGoal';
  options: Array<{
    value: string;
    label: string;
    description: string;
  }>;
}

// Define all steps for the service selector
const steps: Step[] = [
  {
    title: 'What is your business size?',
    description: 'Tell us about the scale of your organization.',
    field: 'businessSize',
    options: [
      { value: 'solo', label: 'Working independently or as a freelancer', description: 'Solo Professional' },
      { value: 'small', label: '2-20 employees', description: 'Small Business' },
      { value: 'medium', label: '21-200 employees', description: 'Medium Business' },
      { value: 'enterprise', label: '200+ employees', description: 'Enterprise' },
    ],
  },
  {
    title: 'What is your current AI experience?',
    description: 'Help us understand your current level of AI knowledge.',
    field: 'aiExperience',
    options: [
      { value: 'none', label: 'Never used AI tools professionally', description: 'No Experience' },
      { value: 'beginner', label: 'Used basic AI tools occasionally', description: 'Beginner' },
      { value: 'intermediate', label: 'Regular use of AI in some workflows', description: 'Intermediate' },
      { value: 'advanced', label: 'Extensive AI integration in daily operations', description: 'Advanced' },
    ],
  },
  {
    title: 'What type of implementation do you prefer?',
    description: 'How quickly do you want to implement AI solutions?',
    field: 'implementation',
    options: [
      { value: 'quick', label: 'Ready-to-use solutions with minimal setup', description: 'Quick Start' },
      { value: 'thorough', label: 'Balanced approach with customization', description: 'Thorough' },
      { value: 'comprehensive', label: 'Deep integration with extensive planning', description: 'Comprehensive' },
    ],
  },
  {
    title: 'What is your budget range?',
    description: 'Help us recommend solutions that fit your financial constraints.',
    field: 'budget',
    options: [
      { value: 'low', label: 'Looking for cost-effective solutions', description: 'Limited' },
      { value: 'medium', label: 'Willing to invest in quality solutions', description: 'Moderate' },
      { value: 'high', label: 'Prepared for significant investment in AI', description: 'Substantial' },
    ],
  },
  {
    title: 'What is your primary goal?',
    description: 'What are you hoping to achieve with AI?',
    field: 'primaryGoal',
    options: [
      { value: 'skill-development', label: 'Learn to use AI tools effectively', description: 'Skill Development' },
      { value: 'process-automation', label: 'Streamline workflows and reduce manual tasks', description: 'Process Automation' },
      { value: 'custom-solution', label: 'Build a specialized AI system for specific needs', description: 'Custom AI Solution' },
      { value: 'system-optimization', label: 'Improve existing AI implementations', description: 'System Optimization' },
      { value: 'business-integration', label: 'Integrate AI across your organization', description: 'Business Integration' },
    ],
  },
];

const ServiceSelector: React.FC = () => {
  const { requirements, updateRequirements, recommendation, generateRecommendation, resetSelector } = useServiceSelector();
  const [currentStep, setCurrentStep] = useState(0);
  const [isComplete, setIsComplete] = useState(false);

  // Handle option selection
  const handleSelect = (value: string) => {
    const currentField = steps[currentStep].field;
    
    // Type assertions to ensure proper typing
    if (currentField === 'businessSize') {
      updateRequirements({ businessSize: value as BusinessSize });
    } else if (currentField === 'aiExperience') {
      updateRequirements({ aiExperience: value as AIExperience });
    } else if (currentField === 'implementation') {
      updateRequirements({ implementation: value as Implementation });
    } else if (currentField === 'budget') {
      updateRequirements({ budget: value as Budget });
    } else if (currentField === 'primaryGoal') {
      updateRequirements({ primaryGoal: value as PrimaryGoal });
    }

    // Move to next step or complete
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      generateRecommendation();
      setIsComplete(true);
    }
  };

  // Handle back navigation
  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Handle restart
  const handleRestart = () => {
    resetSelector();
    setCurrentStep(0);
    setIsComplete(false);
  };

  // Current step data
  const currentStepData = steps[currentStep];

  return (
    <div className="w-full max-w-4xl mx-auto bg-white rounded-lg shadow-lg overflow-hidden">
      {/* Progress bar */}
      <div className="w-full bg-gray-200 h-2">
        <div 
          className="bg-gradient-to-r from-blue-500 to-indigo-600 h-2 transition-all duration-500 ease-out"
          style={{ width: `${Math.min(100, (currentStep / (steps.length - 1)) * 100)}%` }}
        ></div>
      </div>
      
      {/* Content area */}
      <div className="p-6 md:p-8">
        {!isComplete ? (
          <>
            <div className="mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">{currentStepData.title}</h3>
              <p className="text-gray-600">{currentStepData.description}</p>
            </div>
            
            <div className="grid gap-4 md:grid-cols-2">
              {currentStepData.options.map((option) => (
                <button
                  key={option.value}
                  onClick={() => handleSelect(option.value)}
                  className="p-4 border-2 border-gray-200 rounded-lg text-left hover:border-blue-300 hover:shadow-md transition-all duration-200 bg-white"
                >
                  <div className="font-medium text-lg mb-1 text-gray-900">{option.description}</div>
                  <div className="text-sm text-gray-600">{option.label}</div>
                </button>
              ))}
            </div>
            
            {/* Navigation buttons */}
            <div className="mt-8 flex justify-between items-center">
              {currentStep > 0 ? (
                <button 
                  onClick={handleBack}
                  className="px-5 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition-colors"
                >
                  Back
                </button>
              ) : (
                <div></div>
              )}
              <div className="text-center">
                <span className="text-gray-500">
                  Step {currentStep + 1} of {steps.length}
                </span>
              </div>
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Your Recommended Service</h3>
            
            {recommendation && (
              <div className="text-center mb-8">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6 w-full">
                  <h4 className="text-xl font-bold text-blue-700 mb-2">{recommendation.primaryService}</h4>
                  <p className="text-gray-700 mb-4">{recommendation.description}</p>
                  <Link 
                    to={recommendation.link}
                    className="inline-block px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                  >
                    Learn More
                  </Link>
                </div>
                
                <div className="mb-6">
                  <h5 className="text-lg font-semibold text-gray-800 mb-2">You might also consider:</h5>
                  <p className="font-medium text-gray-700">{recommendation.secondaryService}</p>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-4">
                  <button
                    onClick={handleRestart}
                    className="px-6 py-3 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
                  >
                    Start Over
                  </button>
                  <Link
                    to="/contact"
                    className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                  >
                    Book a Consultation
                  </Link>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ServiceSelector;